import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, CheckCircle, ArrowRight, XCircle, Heart, Brain, 
  Zap, Moon, Loader2, Quote, MapPin, Clock, Shield
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FORMSPREE_ID = 'mvkpjgoe';

const frustrations = [
  { text: "The mask makes me claustrophobic", icon: XCircle },
  { text: "I rip it off in my sleep without realizing", icon: Moon },
  { text: "The noise keeps my partner awake", icon: XCircle },
  { text: "It dries out my nose and throat", icon: XCircle },
  { text: "I can't find a mask that doesn't leak", icon: XCircle },
  { text: "Traveling with it is impossible", icon: XCircle },
  { text: "I feel like I'm suffocating", icon: XCircle },
  { text: "It's ruining my intimacy/relationship", icon: Heart },
];

const testimonials = [
  {
    quote: "I tried 4 different CPAP masks over 2 years. Nothing worked. Within a week of getting my oral appliance from Dr. Yang, I was sleeping through the night. It changed my life.",
    name: "Robert K.",
    location: "Roseville",
    status: "Former CPAP user for 2 years"
  },
  {
    quote: "My husband's CPAP was so loud I started sleeping in the guest room. Since switching to the oral appliance, we're back in the same bed and both sleeping great.",
    name: "Linda M.",
    location: "Rocklin",
    status: "Husband switched from CPAP"
  },
  {
    quote: "I travel constantly for work. The CPAP was a dealbreaker. Now I just toss my oral device in my bag and go. Best decision I ever made.",
    name: "James T.",
    location: "Folsom",
    status: "Frequent business traveler"
  },
];

const CPAPIntolerant = () => {
  const [formData, setFormData] = useState({ firstName: '', email: '', phone: '', struggle: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedFrustrations, setSelectedFrustrations] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'CPAP Intolerant? Try Oral Appliance Therapy | Sunshine Dental Care';
  }, []);

  const toggleFrustration = (text) => {
    setSelectedFrustrations(prev => 
      prev.includes(text) ? prev.filter(f => f !== text) : [...prev, text]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'CPAP Intolerant Lead - HIGH PRIORITY',
          'Lead Type': 'CPAP Intolerant Page',
          'CPAP Frustrations': selectedFrustrations.join(', '),
          'Main Struggle': formData.struggle,
          ...formData,
        }),
      });
      setIsSubmitted(true);
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="cpap-intolerant-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-red-900 via-red-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=1920')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-200 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <XCircle size={16} />
              <span>Can't Tolerate CPAP?</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              You Tried CPAP. <br />
              <span className="text-red-300">It Didn't Work Out.</span><br />
              <span className="text-cyan-400">Here's What's Next.</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              You're not a failure. You're not "non-compliant." You're just human — and CPAP isn't for everyone. 
              There's a better option, and Dr. Maichou Yang can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#get-help"
                className="inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-8 py-4 rounded-xl transition-all"
              >
                Find My CPAP Alternative
                <ArrowRight size={20} />
              </a>
              <a
                href="tel:9167731122"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-xl transition-all border border-white/20"
              >
                <Phone size={20} />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Validation Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            Does Any of This Sound Familiar?
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Select all that apply to you. (You're definitely not alone.)
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {frustrations.map((item, idx) => {
              const isSelected = selectedFrustrations.includes(item.text);
              return (
                <button
                  key={idx}
                  onClick={() => toggleFrustration(item.text)}
                  className={`flex items-center gap-4 p-4 rounded-xl text-left transition-all ${
                    isSelected 
                      ? 'bg-red-100 border-2 border-red-400 shadow-md' 
                      : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`frustration-${idx}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {isSelected ? <CheckCircle size={20} /> : <item.icon size={20} />}
                  </div>
                  <span className={`font-medium ${isSelected ? 'text-red-800' : 'text-gray-700'}`}>
                    {item.text}
                  </span>
                </button>
              );
            })}
          </div>
          {selectedFrustrations.length > 0 && (
            <div className="mt-8 p-6 bg-cyan-50 border border-cyan-200 rounded-xl text-center animate-in fade-in slide-in-from-top-2">
              <p className="text-cyan-800 font-medium">
                You selected {selectedFrustrations.length} frustration{selectedFrustrations.length > 1 ? 's' : ''}. 
                <strong> You're an excellent candidate for oral appliance therapy.</strong>
              </p>
              <a href="#get-help" className="inline-flex items-center gap-2 mt-4 text-cyan-600 hover:text-cyan-700 font-semibold">
                See your options below
                <ArrowRight size={16} />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* You're Not Alone Stats */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            The CPAP Struggle Is Real (And Common)
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <p className="text-5xl font-bold text-red-500 mb-2">50%</p>
              <p className="text-gray-600">of CPAP users quit within the first year</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-amber-500 mb-2">30%</p>
              <p className="text-gray-600">never even fill their CPAP prescription</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-cyan-500 mb-2">90%+</p>
              <p className="text-gray-600">of oral appliance users wear it every night</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Alternative */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The CPAP Alternative Your Sleep Doctor Didn't Mention
            </h2>
            <p className="text-xl text-cyan-100">
              Custom oral appliances: small, comfortable devices you actually use.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Moon, title: "Wear It All Night", desc: "No mask to rip off. No claustrophobia. Just sleep." },
              { icon: Shield, title: "Silent Operation", desc: "Zero noise. Your partner will finally sleep too." },
              { icon: Zap, title: "Travel Anywhere", desc: "Fits in your pocket. No power needed. TSA-friendly." },
              { icon: Brain, title: "Feel Rested", desc: "Wake up with energy instead of headaches and fog." },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                  <item.icon size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-cyan-100">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/cpap-vs-oral-appliance"
              className="inline-flex items-center gap-2 bg-white text-cyan-700 font-bold px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors"
            >
              See Full Comparison
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Former CPAP Users Who Found Relief
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <Quote size={32} className="text-cyan-200 mb-4" />
                <p className="text-gray-700 mb-6 italic">"{t.quote}"</p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-gray-900">{t.name}</p>
                  <p className="text-sm text-gray-500">{t.location}</p>
                  <p className="text-xs text-cyan-600 mt-1">{t.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section id="get-help" className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Finally Sleep Well?
              </h2>
              <p className="text-gray-300 mb-6">
                Dr. Maichou Yang has helped hundreds of CPAP-intolerant patients find relief with custom oral appliances. 
                Schedule your free consultation — no pressure, just answers.
              </p>
              <div className="space-y-4 text-gray-300 mb-8">
                <div className="flex items-center gap-3">
                  <MapPin size={20} className="text-cyan-400" />
                  <span>700 Sunrise Ave, Ste E, Roseville, CA</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={20} className="text-cyan-400" />
                  <span>(916) 773-1122</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-cyan-400" />
                  <span>Mon-Wed: 7:00 AM - 3:30 PM</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl" data-testid="intolerant-form">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Get Your Free Consultation</h3>
                  <p className="text-gray-500 text-sm mb-4">We'll help you find the right solution.</p>
                  
                  <input
                    type="text"
                    placeholder="First Name"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  />
                  <textarea
                    placeholder="What's your biggest struggle with CPAP? (optional)"
                    rows={3}
                    value={formData.struggle}
                    onChange={(e) => setFormData({...formData, struggle: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none resize-none"
                  />

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-bold py-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <><Loader2 size={20} className="animate-spin" /> Sending...</>
                    ) : (
                      <>Get My Free Consultation</>
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-8" data-testid="form-success">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">We Got Your Message!</h3>
                  <p className="text-gray-600">Someone from our team will reach out within 24 hours. You're one step closer to better sleep.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CPAPIntolerant;
